import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Edit, Trash2, CheckCircle, XCircle, Download, Plus } from 'lucide-react';
import { coldStoragesApi } from '../../lib/api';
import PageHeader from '../../components/PageHeader';
import DataTable from '../../components/DataTable';
import Modal from '../../components/Modal';

interface ColdStorage {
  id: string;
  storageCode: string;
  name: string;
  storageCategory: string;
  address: string;
  phone: string;
  fax: string;
  storageFeeRate: string;
  isActive: boolean;
}

export default function ColdStorageList() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingStorage, setEditingStorage] = useState<ColdStorage | null>(null);
  const [formData, setFormData] = useState({
    storageCode: '',
    name: '',
    storageCategory: 'both',
    address: '',
    phone: '',
    fax: '',
    storageFeeRate: '',
    remarks: '',
  });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['cold-storages', search],
    queryFn: () => coldStoragesApi.getAll({ search }),
  });

  const createMutation = useMutation({
    mutationFn: coldStoragesApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cold-storages'] });
      toast.success('冷蔵庫を登録しました');
      setIsModalOpen(false);
      resetForm();
    },
    onError: () => toast.error('登録に失敗しました'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => coldStoragesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cold-storages'] });
      toast.success('冷蔵庫を更新しました');
      setIsModalOpen(false);
      resetForm();
    },
    onError: () => toast.error('更新に失敗しました'),
  });

  const deleteMutation = useMutation({
    mutationFn: coldStoragesApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cold-storages'] });
      toast.success('冷蔵庫を削除しました');
    },
    onError: () => toast.error('削除に失敗しました'),
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      await Promise.all(ids.map(id => coldStoragesApi.delete(id)));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cold-storages'] });
      toast.success('選択した冷蔵庫を削除しました');
    },
    onError: () => toast.error('一括削除に失敗しました'),
  });

  const bulkStatusMutation = useMutation({
    mutationFn: async ({ ids, isActive }: { ids: string[]; isActive: boolean }) => {
      await Promise.all(ids.map(id => coldStoragesApi.update(id, { isActive })));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cold-storages'] });
      toast.success('ステータスを更新しました');
    },
    onError: () => toast.error('ステータス更新に失敗しました'),
  });

  const resetForm = () => {
    setFormData({
      storageCode: '',
      name: '',
      storageCategory: 'both',
      address: '',
      phone: '',
      fax: '',
      storageFeeRate: '',
      remarks: '',
    });
    setEditingStorage(null);
  };

  const handleEdit = (storage: ColdStorage) => {
    setEditingStorage(storage);
    setFormData({
      storageCode: storage.storageCode || '',
      name: storage.name,
      storageCategory: storage.storageCategory,
      address: storage.address || '',
      phone: storage.phone || '',
      fax: storage.fax || '',
      storageFeeRate: storage.storageFeeRate || '',
      remarks: '',
    });
    setIsModalOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingStorage) {
      updateMutation.mutate({ id: editingStorage.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDelete = (id: string) => {
    if (confirm('この冷蔵庫を削除しますか？')) {
      deleteMutation.mutate(id);
    }
  };

  const handleBulkDelete = (ids: string[]) => {
    if (confirm(`${ids.length}件の冷蔵庫を削除しますか？`)) {
      bulkDeleteMutation.mutate(ids);
    }
  };

  const handleBulkActivate = (ids: string[]) => {
    bulkStatusMutation.mutate({ ids, isActive: true });
  };

  const handleBulkDeactivate = (ids: string[]) => {
    bulkStatusMutation.mutate({ ids, isActive: false });
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'chilled': return 'チルド';
      case 'frozen': return '冷凍';
      case 'both': return '両用';
      default: return category;
    }
  };

  const columns = [
    { key: 'storageCode', label: 'コード', width: '100px' },
    { key: 'name', label: '冷蔵庫名', sortable: true },
    {
      key: 'storageCategory',
      label: '種別',
      width: '100px',
      render: (row: ColdStorage) => getCategoryLabel(row.storageCategory),
    },
    { key: 'address', label: '住所' },
    { key: 'phone', label: '電話番号', width: '140px' },
    { key: 'storageFeeRate', label: '保管料単価', width: '120px' },
    {
      key: 'isActive',
      label: '状態',
      width: '80px',
      render: (row: ColdStorage) => (
        <span className={row.isActive ? 'badge badge-success' : 'badge badge-error'}>
          {row.isActive ? '有効' : '無効'}
        </span>
      ),
    },
    {
      key: 'actions',
      label: '操作',
      width: '100px',
      render: (row: ColdStorage) => (
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); handleEdit(row); }}
            className="p-1.5 rounded-lg transition-colors hover:bg-gray-100"
            style={{ color: 'var(--accent-color)' }}
            title="編集"
          >
            <Edit size={16} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); handleDelete(row.id); }}
            className="p-1.5 rounded-lg transition-colors hover:bg-gray-100"
            style={{ color: 'var(--error-color)' }}
            title="削除"
          >
            <Trash2 size={16} />
          </button>
        </div>
      ),
    },
  ];

  const bulkActions = [
    {
      label: '有効化',
      icon: <CheckCircle size={14} />,
      onClick: handleBulkActivate,
      variant: 'primary' as const,
    },
    {
      label: '無効化',
      icon: <XCircle size={14} />,
      onClick: handleBulkDeactivate,
      variant: 'outline' as const,
    },
    {
      label: '一括削除',
      icon: <Trash2 size={14} />,
      onClick: handleBulkDelete,
      variant: 'danger' as const,
    },
  ];

  return (
    <div className="animate-fadeIn">
      <PageHeader
        title="冷蔵庫マスタ"
        breadcrumbs={[{ label: 'マスタ管理' }]}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="冷蔵庫名で検索..."
        onApply={() => refetch()}
        actions={
          <button className="btn btn-primary btn-sm" onClick={() => { resetForm(); setIsModalOpen(true); }}>
            <Plus size={14} />
            冷蔵庫登録
          </button>
        }
      />

      <DataTable
        columns={columns}
        data={data?.data?.data || []}
        loading={isLoading}
        selectable
        bulkActions={bulkActions}
        getRowId={(row: ColdStorage) => row.id}
        emptyMessage="冷蔵庫が登録されていません"
        defaultSortKey="name"
        defaultSortOrder="asc"
      />

      <Modal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); resetForm(); }}
        title={editingStorage ? '冷蔵庫編集' : '冷蔵庫登録'}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">冷蔵庫コード</label>
              <input
                type="text"
                className="input-field"
                value={formData.storageCode}
                onChange={(e) => setFormData({ ...formData, storageCode: e.target.value })}
              />
            </div>
            <div>
              <label className="label">種別 <span className="text-red-500">*</span></label>
              <select
                className="input-field"
                value={formData.storageCategory}
                onChange={(e) => setFormData({ ...formData, storageCategory: e.target.value })}
                required
              >
                <option value="chilled">チルド</option>
                <option value="frozen">冷凍</option>
                <option value="both">両用</option>
              </select>
            </div>
          </div>

          <div>
            <label className="label">冷蔵庫名 <span className="text-red-500">*</span></label>
            <input
              type="text"
              className="input-field"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          <div>
            <label className="label">住所</label>
            <input
              type="text"
              className="input-field"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="label">電話番号</label>
              <input
                type="text"
                className="input-field"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </div>
            <div>
              <label className="label">FAX番号</label>
              <input
                type="text"
                className="input-field"
                value={formData.fax}
                onChange={(e) => setFormData({ ...formData, fax: e.target.value })}
              />
            </div>
            <div>
              <label className="label">保管料単価 (円/kg)</label>
              <input
                type="number"
                step="0.01"
                className="input-field"
                value={formData.storageFeeRate}
                onChange={(e) => setFormData({ ...formData, storageFeeRate: e.target.value })}
              />
            </div>
          </div>

          <div>
            <label className="label">備考</label>
            <textarea
              className="input-field"
              rows={3}
              value={formData.remarks}
              onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t" style={{ borderColor: 'var(--border-color)' }}>
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => { setIsModalOpen(false); resetForm(); }}
            >
              キャンセル
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {editingStorage ? '更新' : '登録'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
